import React, { useMemo } from "react";
import { Input } from "../Input";
import { Button } from "../../Button";
import { Icon } from "../../../Icon";
import {
  EnhancedInputProps,
  isSuffixButton,
  isSuffixIcon,
} from "./EnhancedInput.types";
import "./EnhancedInput.scss";

export const EnhancedInput: React.FC<EnhancedInputProps> = ({
  variant = "primary",
  size = "md",
  value,
  defaultValue,
  placeholder,
  disabled = false,
  readOnly = false,
  clearable = false,
  clearIcon,
  onlyShowClearWhenFocus,
  onClear,
  onChange,
  onFocus,
  onBlur,
  onEnterPress,
  onClick,
  prefixIcon,
  suffixContent,
  suffixIcon,
  onSuffixIconClick,
  minContentWidth = "6.5rem",
  inputStyle,
  className = "",
  testId,
  error = false,
  ...props
}) => {
  // 计算输入框的样式类名
  const inputClass = useMemo(() => {
    const classes = [
      "enhanced-input",
      `enhanced-input--${variant}`,
      `enhanced-input--${size}`,
      disabled && "enhanced-input--disabled",
      readOnly && "enhanced-input--readonly",
      clearable && "enhanced-input--clearable",
      prefixIcon && "enhanced-input--with-prefix",
      (suffixContent || suffixIcon) && "enhanced-input--with-suffix",
      suffixContent &&
        isSuffixButton(suffixContent) &&
        "enhanced-input--with-suffix-button",
      (suffixContent && isSuffixIcon(suffixContent)) || suffixIcon
        ? "enhanced-input--with-suffix-icon"
        : null,
      variant === "warning" && "enhanced-input--warning",
      error && "enhanced-input--error",
      className,
    ];
    return classes.filter(Boolean).join(" ");
  }, [
    variant,
    size,
    disabled,
    readOnly,
    clearable,
    prefixIcon,
    suffixContent,
    suffixIcon,
    error,
    className,
    inputStyle,
    minContentWidth,
  ]);

  // 计算输入框内容区域的样式（与传入的 inputStyle 合并，inputStyle 可覆盖）
  const inputContentStyle = useMemo(() => {
    const style: React.CSSProperties = {};

    // if (prefixIcon) {
    //   // 复杂前缀：默认 8rem，适配注册/登录等「区号 + 分隔 + 图标」等较宽自定义区。
    //   // 仅区号选择器、希望更紧凑的页面（如提现绑卡）请传 complexContentPaddingLeft。
    //   const isComplexComponent = prefixIcon.isComplexComponent === true;
    //   style.paddingLeft = isComplexComponent
    //     ? (prefixIcon.complexContentPaddingLeft ?? "8rem")
    //     : "0.5rem";
    // }

    if (suffixContent) {
      if (!isSuffixButton(suffixContent)) {
        if (isSuffixIcon(suffixContent)) {
          style.paddingRight = "2.5rem";
        } else {
          style.paddingRight = "3.75rem";
        }
      }
    } else if (suffixIcon) {
      // 如果使用 suffixIcon 属性
      style.paddingRight = "2.5rem";
    }

    if (minContentWidth) {
      style.minWidth = minContentWidth;
    }

    return { ...style, ...inputStyle };
  }, [prefixIcon, suffixContent, suffixIcon, minContentWidth, inputStyle]);

  return (
    <div className={inputClass} data-testid={testId} onClick={onClick}>
      {/* 前置图标 */}
      {prefixIcon && (
        <div className="enhanced-input__prefix" onClick={prefixIcon.onClick}>
          {typeof prefixIcon.icon === "string" && !prefixIcon.isImagePath ? (
            // 字符串且不是图片路径 - 使用 Icon 组件
            <Icon
              name={prefixIcon.icon}
              variant={prefixIcon.variant}
              color={prefixIcon.color}
              className={`enhanced-input__prefix-icon ${prefixIcon.className || ""}`}
              size={prefixIcon.size || "md"}
            />
          ) : typeof prefixIcon.icon === "string" && prefixIcon.isImagePath ? (
            // 字符串且是图片路径 - 渲染为 img 标签
            <img
              src={prefixIcon.icon}
              alt="prefix icon"
              className={`enhanced-input__prefix-image ${prefixIcon.className || ""}`}
            />
          ) : (
            // ReactNode - 直接渲染
            <div
              className={`enhanced-input__prefix-custom ${prefixIcon.className || ""}`}
            >
              {prefixIcon.icon}
            </div>
          )}
        </div>
      )}

      {/* 输入框容器 */}
      <div className="enhanced-input__container">
        <Input
          className="enhanced-input__field"
          style={inputContentStyle}
          value={value}
          defaultValue={defaultValue}
          placeholder={placeholder}
          disabled={disabled}
          readOnly={readOnly}
          clearable={clearable}
          clearIcon={clearIcon}
          onlyShowClearWhenFocus={onlyShowClearWhenFocus}
          onClear={onClear}
          onChange={onChange}
          onFocus={onFocus}
          onBlur={onBlur}
          onEnterPress={onEnterPress}
          {...props}
        />
      </div>

      {/* 后置内容 */}
      {(suffixContent || suffixIcon) && (
        <div className="enhanced-input__suffix">
          {suffixIcon ? (
            // 使用简化的 suffixIcon 属性
            <div
              className="enhanced-input__suffix-icon"
              onClick={onSuffixIconClick}
            >
              {suffixIcon}
            </div>
          ) : suffixContent && isSuffixButton(suffixContent) ? (
            <Button
              variant={suffixContent.variant || "primary"}
              size={
                (suffixContent.size === "xs"
                  ? "sm"
                  : suffixContent.size === "xl"
                    ? "lg"
                    : suffixContent.size === "smd"
                      ? "md"
                      : suffixContent.size) || "sm"
              }
              shape={suffixContent.shape || "rounded"}
              disabled={suffixContent.disabled || disabled}
              loading={suffixContent.loading}
              onClick={suffixContent.onClick}
              className={`enhanced-input__suffix-button ${suffixContent.className || ""}`}
            >
              {suffixContent.text}
            </Button>
          ) : suffixContent && isSuffixIcon(suffixContent) ? (
            <div
              className={`enhanced-input__suffix-icon ${suffixContent.className || ""}`}
              onClick={suffixContent.onClick}
              style={suffixContent.style}
            >
              {typeof suffixContent.icon === "string" ? (
                <Icon
                  name={suffixContent.icon}
                  variant={suffixContent.variant}
                  size="sm"
                />
              ) : (
                suffixContent.icon
              )}
            </div>
          ) : (
            suffixContent && (
              <div
                className={`enhanced-input__suffix-unit ${suffixContent.className || ""}`}
              >
                {suffixContent.text}
              </div>
            )
          )}
        </div>
      )}
    </div>
  );
};
